import { useRef } from "react";
import { Button } from "../ui";
import { ctaTranslate } from "../lib/ctaTranslate";
import { useLabel } from "../lib/labels";
import { ProductNameText } from "../lib/ProductNameText";
import { productDisplayName } from "../lib/productText";
import { useSkuCopyToast } from "../lib/skuCopyToast";
import { useDragScrollbar } from "../lib/useDragScrollbar";
import {
  fallbackProductImage,
  handleProductImageError,
  normalizeProductImage,
} from "../lib/fallback-image";
import { navigateExternal } from "../lib/postmessage";
import { recordSummaryViewMore } from "../api/summaryViewMore";
import type { EnergyLabelInfo } from "../types";
import {
  pickFmt,
  pickFmtAmount,
  pickPriceAmt,
  pickPriceSym,
  type PickPriceBlock,
} from "./pick/pickShared";
import { cartSkuForProduct } from "./pick/cartSku";

/* ─── data contracts (백엔드 응답 그대로 유지) ─── */

interface CompareProduct {
  sku: string;
  modelKey?: string;
  modelCode?: string;
  feedSku?: string;
  name?: string;
  price?: PickPriceBlock | number | null;
  image?: string | null;
  energyLabel?: string | EnergyLabelInfo;
  rating?: number | null;
  pdpUrl?: string;
  /** 판매 상태 — "NOT_SOLD"(재고 정보 없음=non-salable)면 price 는 null 로 오고 장바구니 불가. */
  salesStatus?: "SOLD" | "NOT_SOLD";
  /** 재고 여부 — salable(SOLD) 이라도 품절(false)이면 장바구니 버튼 비활성. */
  inStock?: boolean;
  availability?: "purchasable" | "out_of_stock" | "not_for_sale";
}

interface CompareColumn {
  specCode: string;
  label: string;
  unit?: string | null;
  values: Record<string, string>;
  bestSku?: string;
  isInterest?: boolean;
  isRequested?: boolean;
  isDiff?: boolean;
}

interface CompareTableData {
  products?: CompareProduct[];
  columns?: CompareColumn[];
  totalSpecs?: number;
  selectedSpecs?: number;
  suggestions?: string[];
  metadata?: {
    locale?: string;
    skus?: string[];
    preferenceAspects?: string[];
    highlightSku?: string | null;
    highlightColumn?: string | null;
    highlightColumns?: string[];
    comparePageUrl?: string;
    currency?: string;
    symbol?: string;
    [key: string]: unknown;
  };
}

interface Props {
  data: CompareTableData;
  sessionToken: string;
}

/* ─── guide U1 / 3st 제품비교 no.4 (260612: 최대 10개) ─── */

const _MAX_COMPARE_PRODUCTS = 10;

function PdPrice({
  price,
}: {
  price: PickPriceBlock | number | null | undefined;
}) {
  const saveLabel = useLabel("save", "Save");
  const normalized =
    price == null ? undefined : (price as PickPriceBlock | number);
  const priceBlock = typeof normalized === "object" ? normalized : undefined;
  const sym = pickPriceSym(normalized);
  const amount = pickPriceAmt(normalized);
  const originalAmount = priceBlock?.originalAmount;
  const discountPct = priceBlock?.discountPct;
  const hasDiscount =
    originalAmount != null &&
    amount != null &&
    Number.isFinite(originalAmount) &&
    Number.isFinite(amount) &&
    originalAmount > amount;

  const saveAmount = hasDiscount ? originalAmount! - amount! : 0;

  return (
    <div className="pd-price">
      {hasDiscount && (
        <div className="price-member">
          <del>{pickFmt(originalAmount, sym)}</del>
          <span className="txt">
            {discountPct && discountPct > 0
              ? `${saveLabel} ${discountPct}%`
              : `${saveLabel} ${pickFmtAmount(saveAmount, sym)}`}
          </span>
        </div>
      )}
      <div className="price-purchase">{pickFmt(amount, sym)}</div>
    </div>
  );
}

/** 기획 no.4 / COMPARE_LANE §4-5: 관심·요청 spec 열만 interest-col 배경. has-bg(홀짝 줄무)는 guide mock 전용. */
function specCellClasses(
  col: CompareColumn,
  highlightCols: Set<string>,
): string {
  const interest = !!col.isInterest || !!col.isRequested || highlightCols.has(col.specCode);
  return interest ? "interest-col" : "";
}

export default function CompareTableCard({ data, sessionToken }: Props) {
  const products = (data.products || []).slice(0, _MAX_COMPARE_PRODUCTS);
  const columns = data.columns || [];
  const meta = data.metadata || {};
  const highlightCols = new Set(meta.highlightColumns || []);
  const scrollRef = useRef<HTMLDivElement>(null!);
  const barRef = useRef<HTMLDivElement>(null!);
  const trackRef = useRef<HTMLDivElement>(null!);
  const scrollbarRef = useRef<HTMLDivElement>(null!);
  useDragScrollbar(
    scrollRef,
    barRef,
    trackRef,
    scrollbarRef,
    [products.length, columns.length],
  );

  const { copySku } = useSkuCopyToast();
  const productColLabel = useLabel("product", "Product");
  const addLabel = useLabel("add", "Add");
  const noProductsLabel = useLabel("noProducts", "No products to compare.");
  const outOfStockLabel = useLabel("outOfStock", "Out of stock");
  const notForSaleLabel = useLabel("notForSale", "Not for sale");
  const productKey = (p: CompareProduct): string => p.modelKey || p.sku;

  // AISASSIT-100: 백엔드는 동점이어도 bestSku 를 1개만 주므로 그대로 쓰면
  // 같은 값인데 한 제품에만 볼드(best spec)가 걸린다. best 값과 동일한 값을
  // 가진 제품을 모두 볼드 처리하고, 모든 제품 값이 동일하면(차별점 없음) 볼드 제거.
  const bestSkusOf = (col: CompareColumn): Set<string> => {
    if (!col.bestSku) return new Set();
    const bestVal = (col.values[col.bestSku] ?? "").trim();
    if (!bestVal) return new Set();
    const tied = products.filter(
      (p) => (col.values[productKey(p)] ?? "").trim() === bestVal,
    );
    if (tied.length >= products.length) return new Set();
    return new Set(tied.map(productKey));
  };
  const bestSkuSets = new Map<string, Set<string>>(
    columns.map((col) => [col.specCode, bestSkusOf(col)]),
  );

  const handlePdpNavigate = (p: CompareProduct) => {
    const displayName = productDisplayName(p.name, p.sku);
    if (p.sku && sessionToken) {
      recordSummaryViewMore(sessionToken, p.sku);
    }
    if (p.pdpUrl) {
      navigateExternal(p.pdpUrl, undefined, { sessionId: sessionToken });
      return;
    }
    void ctaTranslate({
      code: "compare.tell_me_more_about",
      params: { name: displayName },
      sessionToken,
      fallback: `Tell me more about ${displayName}`,
    });
  };

  const handleSkuCopy = (sku: string) => {
    if (sku) {
      copySku(sku);
    }
  };

  // non-salable(NOT_SOLD) 또는 품절(inStock === false)이면 장바구니 담기 불가.
  const availabilityLabel = (p: CompareProduct) => {
    if (p.availability === "out_of_stock") return outOfStockLabel;
    if (p.availability === "not_for_sale") return notForSaleLabel;
    return "";
  };

  const cartDisabled = (p: CompareProduct) =>
    p.availability === "out_of_stock"
    || p.availability === "not_for_sale"
    || p.salesStatus === "NOT_SOLD"
    || p.inStock === false;

  const handleAddToBasket = (p: CompareProduct) => {
    // 버튼 disabled 와 별개의 2차 가드.
    if (cartDisabled(p)) {
      return;
    }
    const displayName = productDisplayName(p.name, p.sku);
    const cartSku = cartSkuForProduct(p);
    void ctaTranslate({
      code: "compare.add_to_basket",
      params: { name: displayName, sku: p.sku },
      sessionToken,
      fallback: `Add ${displayName} to Basket`,
      toolCall: {
        tool: "commerce_add_to_cart",
        args: { sku: cartSku },
      },
    });
  };

  return (
    <div className="ai-gp1--section">
      <div
        ref={scrollRef}
        className="ai-gp1-scroll--container"
        style={{ overscrollBehaviorX: "contain", touchAction: "pan-x" }}
      >
        {products.length === 0 ? (
          <div className="ai-gp1-table--list" style={{ width: "100%" }}>
            <p style={{ padding: "24px", textAlign: "center", color: "#646464" }}>
              {noProductsLabel}
            </p>
          </div>
        ) : (
        <div className="ai-gp1-table--list">
          <table className="gp1-table">
            <thead>
              <tr>
                <th scope="col" style={{ minWidth: "204px" }}>
                  <span>{productColLabel}</span>
                </th>
                {columns.map((col) => (
                  <th
                    key={col.specCode}
                    scope="col"
                    className={specCellClasses(col, highlightCols) || undefined}
                    style={{ minWidth: "180px" }}
                  >
                    <span>
                      {col.label}
                      {col.unit && ` (${col.unit})`}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {products.map((p) => {
                const imgSrc = p.image
                  ? normalizeProductImage(p.image)
                  : fallbackProductImage();
                const displayName = productDisplayName(p.name, p.sku);

                return (
                  <tr key={productKey(p)}>
                    <td scope="row">
                      <div className="pd-inner">
                        <div className="pd-img">
                          <img
                            src={imgSrc}
                            alt={displayName}
                            loading="lazy"
                            draggable={false}
                            onError={handleProductImageError}
                          />
                        </div>
                        <div className="pd-info">
                          <div className="pd-sku">
                            <button
                              type="button"
                              onClick={() => handleSkuCopy(p.sku)}
                              title={`Copy SKU: ${p.sku}`}
                            >
                              {p.sku}
                            </button>
                          </div>
                          <div className="pd-ufn">
                            <button
                              type="button"
                              className="pd-ufn-txt"
                              onClick={() => handlePdpNavigate(p)}
                            >
                              <ProductNameText name={p.name} sku={p.sku} />
                            </button>
                          </div>
                          <PdPrice price={p.price} />
                          {availabilityLabel(p) ? (
                            <div className="product-availability">{availabilityLabel(p)}</div>
                          ) : null}
                          {cartDisabled(p) ? null : (
                            <div className="pd-btns">
                              <Button
                                variant="primary"
                                size="xxs"
                                className="btn-cart"
                                icon
                                onClick={() => handleAddToBasket(p)}
                              >
                                {addLabel}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {columns.map((col) => {
                      const key = productKey(p);
                      const val = col.values[key] || "—";
                      const isBest =
                        bestSkuSets.get(col.specCode)?.has(key) ?? false;
                      const tdClass = specCellClasses(col, highlightCols);
                      const cellClass = isBest
                        ? "pd-cell highlight"
                        : "pd-cell";
                      return (
                        <td
                          key={col.specCode}
                          className={tdClass || undefined}
                        >
                          <div className={cellClass}>{val}</div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>
      <div ref={scrollbarRef} className="ai-gp1-custom--scrollbar hidden">
        <div ref={trackRef} className="ai-gp1-scrollbar--track">
          <div ref={barRef} className="ai-gp1-scrollbar--bar" />
        </div>
      </div>
    </div>
  );
}
